import { Banknote, CheckCircle2, CircleCheckBig, Clock3, Download, FileSpreadsheet, MoreHorizontal, Plus, Wallet } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { PageHeader } from '@/components/common/PageHeader'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Select } from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { fmtMoney } from '@/features/shared/format'
import { useAuth } from '@/lib/auth/AuthProvider'
import { HrSurface } from './components/HrSurface'
import { KpiCard } from './components/KpiCard'
import { OdemeDetailCard } from './components/odeme/OdemeDetailCard'
import { OdemePlanModal } from './components/odeme/OdemePlanModal'
import { OdemeSideCards } from './components/odeme/OdemeSideCards'
import { OdemeTable } from './components/odeme/OdemeTable'
import { confirmModal } from '@/components/ui/modal'
import { apiErrorMessage } from '@/i18n'
import { useDownloadBankFile, useMarkPaymentsPaid, usePayments } from './api'
import { exportOdemeCsv, fileStamp } from './lib/exportActions'
import { todayIso } from './lib/fileExport'
import type { OdemeDurum, OdemeSatir, OdemeTur } from './types'
import { HR_PAYMENT_WRITE_PERM, HR_SENSITIVE_READ_PERM } from '../permissions'

const DURUMLAR: OdemeDurum[] = ['planlandi', 'onay_bekliyor', 'odendi']

const sum = (list: OdemeSatir[]) => list.reduce((n, o) => n + Number(o.tutar), 0).toFixed(2)

export function OdemelerPage() {
  const { t } = useTranslation()
  const { can } = useAuth()
  const canWrite = can(HR_PAYMENT_WRITE_PERM)
  const payments = usePayments()
  const markPaid = useMarkPaymentsPaid()
  const bankFile = useDownloadBankFile()
  // Banka dosyası açık IBAN içerir: ödeme yazma + hassas okuma yetkisi gerekir (BE ile aynı kural).
  const canBankFile = canWrite && can(HR_SENSITIVE_READ_PERM)
  const all = payments.data ?? []
  const [tab, setTab] = useState<'all' | OdemeTur>('maas')
  const [selectedId, setSelectedId] = useState<string | null>(all[0]?.id ?? null)
  const [donem, setDonem] = useState('all')
  const [birim, setBirim] = useState('all')
  const [durum, setDurum] = useState<'all' | OdemeDurum>('all')
  const [planOpen, setPlanOpen] = useState(false)

  const donemler = useMemo(() => [...new Set(all.map((o) => o.donem))], [all])
  const birimler = useMemo(() => [...new Set(all.map((o) => o.birim))].sort((a, b) => a.localeCompare(b, 'tr')), [all])

  const rows = useMemo(
    () =>
      all.filter(
        (o) =>
          (tab === 'all' || o.tur === tab) &&
          (donem === 'all' || o.donem === donem) &&
          (birim === 'all' || o.birim === birim) &&
          (durum === 'all' || o.durum === durum),
      ),
    [all, tab, donem, birim, durum],
  )

  const kpi = useMemo(() => {
    const by = (d: OdemeDurum) => all.filter((o) => o.durum === d)
    const avans = all.filter((o) => o.tur === 'avans')
    return { planlandi: by('planlandi'), odendi: by('odendi'), bekleyen: by('onay_bekliyor'), avans }
  }, [all])

  const selected = all.find((o) => o.id === selectedId) ?? null
  const adet = (n: number) => `${n} ${t('hr.personel.odemeGecmis.odeme')}`

  return (
    <HrSurface>
      <PageHeader
        title={t('hr.personel.odemeler.title')}
        description={t('hr.personel.odemeler.subtitle')}
        actions={
          <>
            {canBankFile ? (
              <Button
                variant="outline"
                size="sm"
                className="bg-card shadow-xs"
                disabled={bankFile.isPending}
                onClick={() =>
                  bankFile.mutate(donem === 'all' ? undefined : all.find((o) => o.donem === donem)?.periyot, {
                    onError: (e) => toast.error(apiErrorMessage(e)),
                  })
                }
              >
                <Download />
                {t('hr.personel.odemeler.bankaDosyasi')}
              </Button>
            ) : null}
            {canWrite ? (
              <Button size="sm" className="shadow-sm" onClick={() => setPlanOpen(true)}>
                <Plus />
                {t('hr.personel.odemeler.yeni')}
              </Button>
            ) : null}
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" className="bg-card shadow-xs" aria-label={t('hr.personel.detail.dahaFazla')}>
                  <MoreHorizontal />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-60 rounded-xl">
                <DropdownMenuItem onSelect={() => exportOdemeCsv(`odemeler_${fileStamp()}.csv`, all)}>
                  <FileSpreadsheet />
                  {t('hr.personel.actions.odeme.exportAll')}
                </DropdownMenuItem>
                {canWrite ? (
                  <DropdownMenuItem
                    onSelect={() => {
                      // Yalnızca patron onaylı ve henüz ödenmemiş kayıtlar ödenebilir.
                      const planned = all.filter((row) => row.onaylandi && row.durum !== 'odendi')
                      if (planned.length === 0) {
                        toast.info(t('hr.personel.actions.odeme.nothingToPay'))
                        return
                      }
                      void confirmModal({
                        title: t('confirm.odemePayAll.title'),
                        description: t('confirm.odemePayAll.body', { n: planned.length }),
                        confirmLabel: t('hr.personel.actions.odeme.payAll'),
                      }).then((ok) => {
                        if (!ok) return
                        markPaid.mutate(
                          { rows: planned, paidAt: todayIso() },
                          {
                            onSuccess: () => toast.success(t('hr.personel.actions.odeme.payAllDone', { n: planned.length })),
                            onError: (e) => toast.error(apiErrorMessage(e)),
                          },
                        )
                      })
                    }}
                  >
                    <CircleCheckBig />
                    {t('hr.personel.actions.odeme.payAll')}
                  </DropdownMenuItem>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        }
      />

      <div className="grid gap-3 sm:grid-cols-2 sm:gap-4 xl:grid-cols-4">
        <KpiCard label={t('hr.personel.odemeler.kpi.planlandi')} value={fmtMoney(sum(kpi.planlandi))} hint={adet(kpi.planlandi.length)} tint="info" icon={<Clock3 className="size-5" />} />
        <KpiCard label={t('hr.personel.odemeler.kpi.odendi')} value={fmtMoney(sum(kpi.odendi))} hint={adet(kpi.odendi.length)} tint="success" icon={<CheckCircle2 className="size-5" />} />
        <KpiCard label={t('hr.personel.odemeler.kpi.bekleyen')} value={fmtMoney(sum(kpi.bekleyen))} hint={adet(kpi.bekleyen.length)} tint="warning" icon={<Wallet className="size-5" />} />
        <KpiCard label={t('hr.personel.odemeler.kpi.avans')} value={fmtMoney(sum(kpi.avans))} hint={`${kpi.avans.length} ${t('hr.personel.odemeGecmis.aktifAvans')}`} tint="accent" icon={<Banknote className="size-5" />} />
      </div>

      <div className="grid grid-cols-[minmax(0,1fr)] gap-5 xl:grid-cols-[minmax(0,1fr)_280px]">
        <div className="min-w-0 space-y-4">
          <Tabs value={tab} onValueChange={(v) => setTab(v as 'all' | OdemeTur)}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <TabsList>
                <TabsTrigger value="maas">{t('hr.personel.odeme.tur.maas')}</TabsTrigger>
                <TabsTrigger value="avans">{t('hr.personel.odeme.tur.avans')}</TabsTrigger>
                <TabsTrigger value="masraf">{t('hr.personel.odeme.tur.masraf')}</TabsTrigger>
              </TabsList>
              <div className="flex flex-wrap gap-2">
                <Select size="lg" className="w-auto min-w-[9.5rem] rounded-xl" value={donem} onChange={setDonem} options={[{ value: 'all', label: t('hr.personel.odemeler.allDonem') }, ...donemler.map((d) => ({ value: d, label: d }))]} />
                <Select size="lg" className="w-auto min-w-[9.5rem] rounded-xl" value={birim} onChange={setBirim} options={[{ value: 'all', label: t('hr.personel.filters.allDeps') }, ...birimler.map((b) => ({ value: b, label: b }))]} />
                <Select
                  size="lg"
                  className="w-auto min-w-[9.5rem] rounded-xl"
                  value={durum}
                  onChange={(v) => setDurum(v as 'all' | OdemeDurum)}
                  options={[{ value: 'all', label: t('hr.personel.filters.allDurum') }, ...DURUMLAR.map((d) => ({ value: d, label: t(`hr.personel.odeme.durum.${d}`) }))]}
                />
              </div>
            </div>

            <TabsContent value={tab} className="mt-4">
              <OdemeTable rows={rows} selectedId={selectedId} onSelect={setSelectedId} />
            </TabsContent>
          </Tabs>

          {selected ? <OdemeDetailCard odeme={selected} /> : null}
        </div>

        <OdemeSideCards />
      </div>

      {planOpen ? <OdemePlanModal defaultTur={tab === 'all' ? 'maas' : tab} onClose={() => setPlanOpen(false)} /> : null}
    </HrSurface>
  )
}
